import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  message
} from 'antd'
import dayjs from 'dayjs'
import React, { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '~/components/PageHeader'
import { RegionSelect } from '~/components/RegionSelect'
import { TimezoneSelect } from '~/components/TimezoneSelect'
import { useSoftwaresQuery } from '~/pages/query'
import { showErrors } from '~/utils/helpers'
import { useCreateCourseMutation, useUpdateCourseMutation } from './mutation'
import { useCourseQuery, useUploadLocationsQuery, useUsersQuery } from './query'

export const CreateUpdateCourse: React.FC = () => {
  const { id } = useParams()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { data: course, isFetching: loadingCourse } = useCourseQuery(id)
  const { data: softwares } = useSoftwaresQuery()
  const { data: users } = useUsersQuery()
  const { data: uploadLocations = [], isLoading: loadingUploadLocations } =
    useUploadLocationsQuery()

  const initialValues = useMemo(() => {
    if (course == null) {
      return {
        on_demand: true,
        users_per_server: 4,
        registration_fee: 0,
        currency: 'USD'
      }
    }

    const timezone = course.timezone ?? 'UTC'
    return {
      ...course,
      currency: course.currency ?? 'USD',
      datetime: [
        dayjs(
          new Date(course.start_at).toLocaleString('en', {
            timeZone: timezone
          })
        ),
        dayjs(
          new Date(course.end_at).toLocaleString('en', {
            timeZone: timezone
          })
        )
      ],
      instructors: course.instructors?.map((item) => item.id),
      cas_supports: course.cas_supports?.map((item) => item.id),
      case_uploaders: course.case_uploaders?.map((item) => item.id),
      users: course.doctors?.map((item) => item.id)
    }
  }, [course])

  const updateMutation = useUpdateCourseMutation(id)
  const createMutation = useCreateCourseMutation()
  const mutation = course == null ? createMutation : updateMutation
  const editCourse = useCallback(
    (values) => {
      mutation.mutate(
        { ...values, id },
        {
          onSuccess: () => {
            message.success('Success')
            navigate(-1)
          },
          onError: showErrors
        }
      )
    },
    [mutation, id, navigate]
  )

  if (loadingCourse) {
    return null
  }

  return (
    <>
      <PageHeader
        className='radup-page-header'
        title='Edit course'
        onBack={() => {
          window.history.back()
        }}
      />
      <Form
        layout='vertical'
        form={form}
        onFinish={editCourse}
        initialValues={initialValues}
      >
        <Row gutter={[48, 8]}>
          <Col span={12}>
            <Form.Item
              name='name'
              label='Name'
              rules={[{ required: true, message: 'Please enter course name' }]}
            >
              <Input placeholder='Please enter course name' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='software_id'
              label='Software'
              rules={[{ required: true, message: 'Please select software' }]}
            >
              <Select
                placeholder='Please select software'
                allowClear
                showSearch
                optionFilterProp='label'
                options={softwares?.map((item) => ({
                  label: item.name,
                  value: item.id
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='setup_location'
              label='Set-up location'
              rules={[
                { required: true, message: 'Please select a set-up location' }
              ]}
            >
              <RegionSelect
                rememberSelection={false}
                showRegionCode
                placeholder='Please select a set-up location'
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='upload_location_id'
              label='Upload location'
              rules={[
                { required: true, message: 'Please select an upload location' }
              ]}
            >
              <Select
                placeholder='Please select an upload location'
                loading={loadingUploadLocations}
                allowClear
                showSearch
                optionFilterProp='label'
                notFoundContent={loadingUploadLocations
                  ? 'Loading upload locations...'
                  : 'No upload locations. Add one in Upload Locations.'}
                options={uploadLocations.map((item) => ({
                  label: `${item.name} (${item.bucket})`,
                  value: item.id
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='fleet_type'
              label='Fleet type'
              rules={[
                { required: true, message: 'Please select a fleet type' }
              ]}
            >
              <Select
                placeholder='Please select a fleet type'
                options={[
                  { label: 'g4dn.2xlarge', value: 'g4dn.2xlarge' },
                  { label: 'g4dn.4xlarge', value: 'g4dn.4xlarge' },
                  { label: 'g6f.2xlarge', value: 'g6f.2xlarge' },
                  { label: 'g6f.4xlarge', value: 'g6f.4xlarge' }
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='number_of_users'
              label='Number of users'
              rules={[
                { required: true, message: 'Please enter the number of users' },
                {
                  type: 'integer',
                  min: 1,
                  message: 'Number of users must be a positive integer'
                }
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                placeholder='Please enter the number of users'
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name='datetime'
                  label='Date time'
                  rules={[
                    { required: true, message: 'Please choose the datetime' }
                  ]}
                >
                  <DatePicker.RangePicker
                    style={{ width: '100%' }}
                    showTime={{ format: 'HH:mm' }}
                    showNow
                    format='DD MMMM, YYYY HH:mm'
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='timezone'
                  label='Timezone'
                  rules={[
                    { required: true, message: 'Please select timezone' }
                  ]}
                >
                  <TimezoneSelect placeholder='Please select timezone' />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Form.Item
              name='users'
              label='Add to users'
              rules={[
                {
                  required: true,
                  message: 'Please select a users who manage this course'
                }
              ]}
            >
              <Select
                placeholder='Please select users'
                mode='multiple'
                showSearch
                optionFilterProp='label'
                allowClear
                options={users?.map((item) => ({
                  label: item.name,
                  value: item.id
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='instructors' label='Instructors'>
              <Select
                placeholder='Please select instructors'
                mode='multiple'
                showSearch
                optionFilterProp='label'
                allowClear
                options={users?.map((item) => ({
                  label: item.name,
                  value: item.id
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='case_uploaders' label='Case-uploading'>
              <Select
                placeholder='Please select case uploaders'
                mode='multiple'
                showSearch
                optionFilterProp='label'
                allowClear
                options={users?.map((item) => ({
                  label: item.name,
                  value: item.id
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name='meeting_passcode' label='Zoom passcode'>
              <Input placeholder='Please enter passcode' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='cas_supports' label='CAS Supports'>
              <Select
                placeholder='Please select CAS supports'
                mode='multiple'
                showSearch
                optionFilterProp='label'
                allowClear
                options={users?.map((item) => ({
                  label: item.name,
                  value: item.id
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name='meeting_link' label='Meeting link'>
              <Input placeholder='Please enter url' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='notification_receivers'
              label='Notification receivers'
            >
              <Input placeholder='Please enter notification receivers' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='users_per_server'
              label='Number of users per server'
            >
              <Input placeholder='Empty is unlimited' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='max_number_of_participants'
              label='Close hands-on registration'
            >
              <Input placeholder='Close hands-on registration ' />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item name='registration_fee' label='Price'>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='currency'
                  label='Currency'
                  rules={[
                    { required: true, message: 'Please select currency' }
                  ]}
                >
                  <Select
                    options={[
                      { label: 'USD', value: 'USD' },
                      { label: 'GBP', value: 'GBP' },
                      { label: 'EUR', value: 'EUR' }
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Col span={12}>
          <Form.Item
            name='is_auto_allocate_server'
            valuePropName='checked'
            label='Has server load balancing?'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='on_demand'
            valuePropName='checked'
            label='Is on-demand?'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='sync_servers_enabled'
            valuePropName='checked'
            label='Enable daily syncing servers'
          >
            <Switch />
          </Form.Item>
        </Col>

        <Row gutter={16}>
          <Col span={24}>
            <Button
              type='primary'
              htmlType='submit'
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              Submit
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  )
}
